<template>
  <overall-settings :fields="fields">
    <section v-if="fields" :class="$style['c-split-content-banner']" ref="sectionRef">
      <ContentGrid :desktop-default-grid="[2, 20]" :mobile-default-grid="[1, 14]">
        <div :class="$style['c-split-content-banner__inner']">
          <div
            ref="leftRef"
            :class="[
              $style['c-split-content-banner__left'],
              isLeftVisible && $style['c-split-content-banner__left--revealed'],
            ]"
          >
            <h2 :class="$style['c-split-content-banner__title']">
              {{
                utils.sc.getFieldValue(
                  fields,
                  'title',
                  'Ready to start your gene synthesis project?'
                )
              }}
            </h2>
            <p :class="$style['c-split-content-banner__description']">
              {{
                utils.sc.getFieldValue(
                  fields,
                  'description',
                  'Get an instant quote online, or talk to one of our gene synthesis specialists. Most quotes delivered same day.'
                )
              }}
            </p>

            <div :class="$style['c-split-content-banner__buttons']">
              <SiteButton
                v-for="(button, index) in buttons"
                :key="button.id || `split-banner-button-${index}`"
                v-bind="button"
                size="small"
              />
            </div>

            <p :class="$style['c-split-content-banner__helper']">
              {{ utils.sc.getFieldValue(fields, 'helperPrefix', 'Returning customer?') }}
              <SiteLink :link="utils.sc.getFieldObj(fields, 'helperLink')">
                {{ utils.sc.getFieldValue(fields, 'helperLinkText', 'Reorder from history') }}
              </SiteLink>
            </p>
          </div>

          <div
            ref="rightRef"
            :class="[
              $style['c-split-content-banner__right'],
              isRightVisible && $style['c-split-content-banner__right--revealed'],
            ]"
            aria-hidden="true"
          >
            <PlasmidShapeBox />
          </div>
        </div>
      </ContentGrid>
    </section>
  </overall-settings>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref } from 'vue';
import SiteButton from '@/elements/SiteButton.vue';
import utils from '@/utils';
import { SplitContentBannerProps } from '@/types/components/split-content-banner';
import PlasmidShapeBox from '@/sections/PlasmidShapeBox.vue';
import OverallSettings from '@/sections/overall-settings.vue';

const props = defineProps<SplitContentBannerProps>();
const fields = computed(() => props.fields);
const sectionRef = ref<HTMLElement | null>(null);
const leftRef = ref<HTMLElement | null>(null);
const rightRef = ref<HTMLElement | null>(null);
const isLeftVisible = ref(false);
const isRightVisible = ref(false);

const buttons = computed<any[]>(() => {
  const list = utils.sc.getFieldObj(fields.value, 'buttons');
  return Array.isArray(list) ? list : [];
});

const helperLink = computed(() => {
  const link = utils.sc.getFieldObj(fields.value, 'helperLinkUrl')?.value || {};
  return {
    href: link.href || '#',
    target: link.target || '_self',
  };
});

const getButtonType = (button: any) => {
  const raw = String(utils.sc.getFieldValue(button, 'buttonType', 'primary') || '').toLowerCase();
  return raw === 'secondary' || raw === 'tertiary' ? raw : 'primary';
};

let leftObserver: IntersectionObserver | null = null;
let rightObserver: IntersectionObserver | null = null;

onMounted(() => {
  if (leftRef.value) {
    leftObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isLeftVisible.value = entry.isIntersecting;
        });
      },
      { threshold: 0.12 }
    );
    leftObserver.observe(leftRef.value);
  }

  if (rightRef.value) {
    rightObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          isRightVisible.value = entry.isIntersecting;
        });
      },
      { threshold: 0.12 }
    );
    rightObserver.observe(rightRef.value);
  }
});

onUnmounted(() => {
  if (leftObserver) leftObserver.disconnect();
  if (rightObserver) rightObserver.disconnect();
});
</script>

<style module lang="scss">
@import '../../styles/variables';
.c-split-content-banner {
}

.c-split-content-banner__inner {
  max-width: 1440px;
  margin: 0 auto;
  background: $white;
  display: grid;
  grid-template-columns: 1.06fr 1fr;
  overflow: hidden;
}

.c-split-content-banner__left {
  padding: 60px 56px 60px 0;

  .c-split-content-banner__title,
  .c-split-content-banner__description,
  .c-split-content-banner__buttons,
  .c-split-content-banner__helper {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(8px);
  }
}

.c-split-content-banner__left--revealed {
  .c-split-content-banner__title {
    animation: scb-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 40ms;
  }
  .c-split-content-banner__description {
    animation: scb-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 140ms;
  }
  .c-split-content-banner__buttons {
    animation: scb-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 220ms;
  }
  .c-split-content-banner__helper {
    animation: scb-reveal-up 820ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
    animation-delay: 300ms;
  }
}

.c-split-content-banner__title {
  margin: 0;
  max-width: 540px;
  color: #030514;
  font-size: clamp(40px, 2.8vw, 56px);
  line-height: 1.08;
  font-weight: 700;
  letter-spacing: -0.02em;
}

.c-split-content-banner__description {
  margin: 18px 0 0;
  max-width: 560px;
  color: #282d3f;
  font-size: clamp(17px, 1.15vw, 22px);
  line-height: 1.4;
}

.c-split-content-banner__buttons {
  margin-top: 24px;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.c-split-content-banner__helper {
  margin: 18px 0 0;
  color: #0a0d1f;
  font-size: clamp(14px, 1vw, 18px);
  line-height: 1.4;
}

.c-split-content-banner__helper a {
  margin-left: 6px;
  color: #0b46f4;
  font-weight: 600;
  text-decoration: none;
}

.c-split-content-banner__helper a:hover {
  text-decoration: underline;
}

.c-split-content-banner__right {
  position: relative;
  min-height: 350px;
  opacity: 0;
  transform: translateY(24px);
  filter: blur(8px);
}

.c-split-content-banner__right--revealed {
  animation: scb-reveal-up 860ms cubic-bezier(0.18, 0.8, 0.2, 1) forwards;
  animation-delay: 160ms;
}

.c-split-content-banner__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: right center;
}

@media (max-width: 900px) {
  .c-split-content-banner__inner {
    width: min(360px, calc(100% - 16px));
    grid-template-columns: 1fr;
  }

  .c-split-content-banner__left {
    padding: 26px 20px 18px;
    text-align: center;
  }

  .c-split-content-banner__title {
    font-size: 43px;
    line-height: 1.06;
    max-width: 100%;
  }

  .c-split-content-banner__description {
    margin-top: 14px;
    font-size: 15px;
    max-width: 100%;
  }

  .c-split-content-banner__buttons {
    margin-top: 18px;
    justify-content: center;
    flex-direction: column;
    gap: 10px;
  }

  .c-split-content-banner__helper {
    margin-top: 14px;
    font-size: 13px;
  }

  .c-split-content-banner__right {
    min-height: 140px;
  }

  .c-split-content-banner__image {
    object-position: center bottom;
  }
}

@keyframes scb-reveal-up {
  0% {
    opacity: 0;
    transform: translateY(24px);
    filter: blur(8px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
    filter: blur(0);
  }
}
</style>
